import * as XLSX from 'xlsx'

import type { BulkRegisterRow } from '@/admin/hooks/use-hoa-communities'

/**
 * Read a CSV or Excel (.xlsx/.xls) file into a grid of string cells (header
 * row included). Generic, reusable for any spreadsheet import - Excel via
 * SheetJS, CSV via the parser below.
 */
export async function readSpreadsheetGrid(file: File): Promise<string[][]> {
  const name = file.name.toLowerCase()
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    // Pick the sheet with the most tabular data - OC's real file keeps the
    // homes on a "CustomerCompany" tab, not the first ("FAQ") tab.
    const toGrid = (sheetName: string): string[][] => {
      const sheet = wb.Sheets[sheetName]
      if (!sheet) return []
      const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        blankrows: false,
        defval: '',
      })
      return grid.map((row) =>
        (row as unknown[]).map((cell) => (cell == null ? '' : String(cell))),
      )
    }
    let best: string[][] = []
    let bestScore = -1
    for (const sheetName of wb.SheetNames) {
      const grid = toGrid(sheetName)
      const cols = grid.reduce((m, r) => Math.max(m, r.length), 0)
      // Wide, multi-row sheets win; ignore single-column (e.g. FAQ) tabs.
      const score = cols >= 2 ? cols * grid.length : 0
      if (score > bestScore) {
        bestScore = score
        best = grid
      }
    }
    return best
  }
  const text = await file.text()
  return parseCsv(text)
}

/**
 * Minimal RFC-4180-ish CSV parser: handles quoted fields, commas inside
 * quotes, escaped quotes (""), and \r\n / \n line endings. Returns a grid of
 * string cells (including the header row). Good enough for HOA unit uploads
 * without pulling in a dependency.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  let i = 0
  const pushField = () => {
    row.push(field)
    field = ''
  }
  const pushRow = () => {
    pushField()
    rows.push(row)
    row = []
  }
  while (i < text.length) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
        continue
      }
      field += c
      i++
      continue
    }
    if (c === '"') {
      inQuotes = true
      i++
      continue
    }
    if (c === ',') {
      pushField()
      i++
      continue
    }
    if (c === '\r') {
      i++
      continue
    }
    if (c === '\n') {
      pushRow()
      i++
      continue
    }
    field += c
    i++
  }
  // Trailing field/row (no final newline)
  if (field.length > 0 || row.length > 0) pushRow()
  // Drop fully-empty trailing rows
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''))
}

/** Normalise a header cell for fuzzy matching. */
function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

// Map of our field -> accepted header aliases (normalised). Partial: object
// fields (e.g. components) are matched separately, not by a single column.
const HEADER_ALIASES: Partial<Record<keyof BulkRegisterRow, string[]>> = {
  label: [
    'unit',
    'unitno',
    'unitnumber',
    'unitapartmenthomeno',
    'apartment',
    'apartmentno',
    'home',
    'homeno',
    'homenumber',
  ],
  // "shinglename"/"shinglecolor" are OC's bulk-registration sheet headers.
  product: ['product', 'producttype', 'productname', 'shinglename'],
  color: ['color', 'colour', 'shinglecolor', 'shinglecolour'],
  contractor: ['contractor', 'installer', 'contractorname'],
  warranty: [
    'warranty',
    'warrantyoption',
    'warrantyplan',
    'plan',
    'coverage',
    'warrantytype',
  ],
  owner_name: ['ownername', 'owner', 'homeowner', 'name'],
  owner_email: [
    'owneremail',
    'email',
    'homeowneremail',
    'emailaddress',
    'customeremailaddress',
    'customeremail',
  ],
  // The property/install address is the per-home location; OC labels it
  // "Installation Address". It also doubles as the unit label.
  address_line1: [
    'address',
    'addressline1',
    'address1',
    'street',
    'streetaddress',
    'correspondenceaddress',
    'installationaddress',
    'installaddress',
  ],
  address_line2: ['addressline2', 'address2', 'installationaddressline2'],
  city: ['city', 'town', 'towncity', 'installationaddresscity'],
  state: ['state', 'province', 'region', 'stateprovince', 'installationaddressstate'],
  zip: [
    'zip',
    'zipcode',
    'postcode',
    'postalcode',
    'postal',
    'installationaddresszipcode',
  ],
  country: ['country', 'mailingaddresscountry', 'countrycode'],
  owner_first_name: ['homeownerfirstname', 'ownerfirstname', 'firstname'],
  owner_last_name: ['homeownerlastname', 'ownerlastname', 'lastname'],
  owner_phone: ['customerphonenumber', 'phone', 'phonenumber', 'customerphone'],
  year_built: ['yearbuilt'],
  property_type: ['propertytype'],
  squares: ['numberofsquaresinstalled', 'numberofsquares', 'squares', 'numsquares'],
  contractor_program_number: ['contractorprogramnumber', 'memberid', 'programnumber'],
  date_installed: [
    'dateinstalled',
    'installdate',
    'installationdate',
    'dateofshingleinstall',
  ],
  mailing_line1: ['mailingaddress', 'mailingaddressline1'],
  mailing_city: ['mailingaddresscity'],
  mailing_state: ['mailingaddressstate'],
  mailing_zip: ['mailingaddresszipcode'],
  mailing_country: ['mailingaddresscountry'],
}

// OC's 5 component-install columns -> our component keys (Yes/No per home).
const COMPONENT_HEADERS: Record<string, string> = {
  owenscorningunderlayment: 'underlayment',
  owenscorningstarterstrip: 'starter_strip',
  owenscorningicewater: 'ice_water',
  owenscorninghipridge: 'hip_ridge',
  owenscorningventilation: 'ventilation',
}

function isYes(v: string): boolean {
  return /^(y|yes|true|1|x)$/i.test(String(v || '').trim())
}

/**
 * OC's sheet has a second "instructions" row under the header (cells like
 * "Mandatory", "Optional", "Must be Single Family Or Other"). Detect such a
 * legend/requirements row so it isn't parsed as a home.
 */
export function isLegendRow(cells: string[]): boolean {
  const nonEmpty = cells.map((c) => String(c || '').trim()).filter(Boolean)
  if (nonEmpty.length < 3) return false
  const legend = nonEmpty.filter((c) =>
    /^(mandatory|optional)$/i.test(c) || /^must\b/i.test(c) || /^provide\b/i.test(c),
  )
  return legend.length >= Math.ceil(nonEmpty.length * 0.4)
}

/**
 * Build a header-index map from the header row, matching each known field to a
 * column by alias. Returns ``{field: columnIndex}`` for matched fields.
 */
export function mapHeaders(header: string[]): Partial<Record<keyof BulkRegisterRow, number>> {
  const map: Partial<Record<keyof BulkRegisterRow, number>> = {}
  header.forEach((cell, idx) => {
    const n = norm(cell)
    for (const field of Object.keys(HEADER_ALIASES) as (keyof BulkRegisterRow)[]) {
      if (map[field] === undefined && HEADER_ALIASES[field].includes(n)) {
        map[field] = idx
      }
    }
  })
  return map
}

/** Parse a register grid (CSV or Excel) into HOA bulk-register rows. */
export function parseHoaGrid(grid: string[][]): {
  rows: BulkRegisterRow[]
  matchedColumns: (keyof BulkRegisterRow)[]
  hasLabel: boolean
} {
  if (grid.length === 0) return { rows: [], matchedColumns: [], hasLabel: false }
  const header = grid[0]
  const map = mapHeaders(header)
  const matched = Object.keys(map) as (keyof BulkRegisterRow)[]
  const get = (cells: string[], field: keyof BulkRegisterRow): string => {
    const idx = map[field]
    return idx === undefined ? '' : (cells[idx] ?? '').trim()
  }
  // Locate OC's 5 component columns by header so each home's Yes/No flags can
  // be read into a components object.
  const componentCols: { key: string; idx: number }[] = []
  header.forEach((cell, idx) => {
    const k = COMPONENT_HEADERS[norm(cell)]
    if (k) componentCols.push({ key: k, idx })
  })
  const rows: BulkRegisterRow[] = grid
    .slice(1)
    .filter((cells) => !isLegendRow(cells))
    .map((cells) => {
      const first = get(cells, 'owner_first_name')
      const last = get(cells, 'owner_last_name')
      const combinedName =
        get(cells, 'owner_name') || [first, last].filter(Boolean).join(' ')
      const addr1 = get(cells, 'address_line1')
      // OC's sheet has no separate unit number - the install address IS the
      // home identifier, so fall back to it (then owner) for the label.
      const label = get(cells, 'label') || addr1 || combinedName
      return {
        label,
        product: get(cells, 'product'),
        color: get(cells, 'color'),
        contractor: get(cells, 'contractor'),
        contractor_program_number: get(cells, 'contractor_program_number'),
        warranty: get(cells, 'warranty'),
        owner_name: combinedName,
        owner_first_name: first,
        owner_last_name: last,
        owner_email: get(cells, 'owner_email'),
        owner_phone: get(cells, 'owner_phone'),
        address_line1: addr1,
        address_line2: get(cells, 'address_line2'),
        city: get(cells, 'city'),
        state: get(cells, 'state'),
        zip: get(cells, 'zip'),
        country: get(cells, 'country'),
        year_built: get(cells, 'year_built'),
        property_type: get(cells, 'property_type'),
        squares: get(cells, 'squares'),
        date_installed: get(cells, 'date_installed'),
        mailing_line1: get(cells, 'mailing_line1'),
        mailing_city: get(cells, 'mailing_city'),
        mailing_state: get(cells, 'mailing_state'),
        mailing_zip: get(cells, 'mailing_zip'),
        mailing_country: get(cells, 'mailing_country'),
        components: componentCols.length
          ? Object.fromEntries(
              componentCols.map((c) => [c.key, isYes(cells[c.idx] ?? '')]),
            )
          : undefined,
      }
    })
    .filter((r) => r.label)
  const hasLabel =
    map.label !== undefined ||
    map.address_line1 !== undefined ||
    map.owner_name !== undefined ||
    map.owner_first_name !== undefined
  return { rows, matchedColumns: matched, hasLabel }
}

/** Parse CSV text into HOA bulk-register rows. Requires a header row. */
export function parseHoaCsv(text: string) {
  return parseHoaGrid(parseCsv(text))
}

/** Parse a register file (CSV or Excel) into HOA bulk-register rows. */
export async function parseHoaFile(file: File) {
  return parseHoaGrid(await readSpreadsheetGrid(file))
}

// ── Bulk claims CSV ─────────────────────────────────────────────────
// A claims upload lists the units (by label) to file a claim against,
// with an optional per-unit description. The shared claim details
// (category, defect type, etc.) are applied to every unit in the dialog.

export interface HoaClaimCsvRow {
  /** Unit label as registered, e.g. "Apt 1203". Matched within the community. */
  label: string
  /** Optional per-unit description; falls back to the shared description. */
  description: string
}

const CLAIM_HEADER_ALIASES: Record<keyof HoaClaimCsvRow, string[]> = {
  label: [
    'unit',
    'unitno',
    'unitnumber',
    'unitapartmenthomeno',
    'apartment',
    'apartmentno',
    'home',
    'homeno',
    'homenumber',
  ],
  description: ['description', 'details', 'notes', 'issue', 'defectdescription'],
}

/** Parse a bulk-claims grid (from CSV or Excel) into rows. */
export function parseHoaClaimsGrid(grid: string[][]): {
  rows: HoaClaimCsvRow[]
  hasLabel: boolean
} {
  if (grid.length === 0) return { rows: [], hasLabel: false }
  const header = grid[0]
  const map: Partial<Record<keyof HoaClaimCsvRow, number>> = {}
  header.forEach((cell, idx) => {
    const n = norm(cell)
    for (const field of Object.keys(CLAIM_HEADER_ALIASES) as (keyof HoaClaimCsvRow)[]) {
      if (map[field] === undefined && CLAIM_HEADER_ALIASES[field].includes(n)) {
        map[field] = idx
      }
    }
  })
  const get = (cells: string[], field: keyof HoaClaimCsvRow): string => {
    const idx = map[field]
    return idx === undefined ? '' : (cells[idx] ?? '').trim()
  }
  const rows: HoaClaimCsvRow[] = grid
    .slice(1)
    .map((cells) => ({
      label: get(cells, 'label'),
      description: get(cells, 'description'),
    }))
    .filter((r) => r.label)
  return { rows, hasLabel: map.label !== undefined }
}

/** Parse a bulk-claims CSV string into rows. */
export function parseHoaClaimsCsv(text: string): {
  rows: HoaClaimCsvRow[]
  hasLabel: boolean
} {
  return parseHoaClaimsGrid(parseCsv(text))
}

/** Parse a bulk-claims file (CSV or Excel) into rows. */
export async function parseHoaClaimsFile(file: File): Promise<{
  rows: HoaClaimCsvRow[]
  hasLabel: boolean
}> {
  return parseHoaClaimsGrid(await readSpreadsheetGrid(file))
}
